import {useMemo} from "react";
import {BookingPostKind, ScheduledPaymentType, useGetBookingByIdQuery} from "../generated/graphql";
import {options} from "../views/PanelCardPayments/AddNamedScheduledPaymentForm";

/**
 * Trennung „Kursgebühr“ vs. „Zusatzposten/Gebühr“ im Buchungseditor.
 *
 * Die Art kommt vom Server (`ScheduledPayment.postKind`, dieselbe Regel wie beim Umbuchen).
 * Vorschau-Zeilen kennen das Feld nicht, behalten aber die Id gespeicherter Posten — daher
 * Nachschlagen per Id in der geladenen Buchung. Zeilen ohne Treffer (neue Buchung, im Formular
 * umgetypte Rate) folgen der Typ-Regel des Servers: AdditionalItem → Zusatzposten, Mahn-/Storno-/
 * Rücklastschrift-Typ → Gebühr, sonst Kursgebühr (Marker kennt der Client nicht).
 */
export type PostKindById = Map<string, BookingPostKind>;

type RateLike = {id?: string | null; type?: ScheduledPaymentType | null; title?: string | null};

export const postKindById = (rates?: Array<{id?: string | null; postKind?: BookingPostKind | null}> | null): PostKindById =>
    new Map((rates ?? []).filter((rate) => rate.id && rate.postKind).map((rate) => [rate.id as string, rate.postKind as BookingPostKind]));

const FEE_TYPES: Array<ScheduledPaymentType> = [
    ScheduledPaymentType.DunningLevel1,
    ScheduledPaymentType.DunningLevel2,
    ScheduledPaymentType.DunningLevel3,
    ScheduledPaymentType.CancelationFee,
    ScheduledPaymentType.Chargeback,
];

const postKindByType = (type?: ScheduledPaymentType | null): BookingPostKind =>
    type === ScheduledPaymentType.AdditionalItem ? BookingPostKind.AdditionalItem : type && FEE_TYPES.includes(type) ? BookingPostKind.Fee : BookingPostKind.CourseFee;

export const postKindOf = (rate: RateLike, kindById: PostKindById): BookingPostKind => (rate.id ? kindById.get(rate.id) : undefined) ?? postKindByType(rate.type);

/** Kursraten behalten ihren Original-Index (Formik-Callbacks arbeiten mit Indizes), Posten kommen in einen eigenen Block. */
export const splitByPostKind = <T extends RateLike>(rates: Array<T>, kindById: PostKindById): {courseFees: Array<{rate: T; index: number}>; posts: Array<{rate: T; index: number}>} => {
    const entries = rates.map((rate, index) => ({rate, index}));

    return {
        courseFees: entries.filter(({rate}) => postKindOf(rate, kindById) === BookingPostKind.CourseFee),
        posts: entries.filter(({rate}) => postKindOf(rate, kindById) !== BookingPostKind.CourseFee),
    };
};

/** Bezeichnung einer Zeile: eigener Titel, sonst das Label des Typs („Kursgebühr“, „Mahnstufe 1“ …). */
export const rateLabel = (rate: RateLike): string => {
    const title = rate.title?.trim();

    if (title) return title;

    return options.find((option) => option.type === (rate.type ?? ScheduledPaymentType.Standard))?.label ?? "Kursgebühr";
};

export const postKindLabel: Record<BookingPostKind, string> = {
    [BookingPostKind.CourseFee]: "Kursgebühr",
    [BookingPostKind.Fee]: "Gebühr",
    [BookingPostKind.AdditionalItem]: "Zusatzposten",
};

/** Standard-Editor: die Buchung liegt bereits im Apollo-Cache (BookingCreate lädt dieselbe Query). */
export const usePostKindById = (bookingId?: string | null): PostKindById => {
    const {data} = useGetBookingByIdQuery({variables: {id: bookingId ?? ""}, skip: !bookingId, fetchPolicy: "cache-first"});

    return useMemo(() => postKindById(data?.bookingById?.scheduledPayments), [data]);
};
